"use client";

import { useState, useEffect } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import Image from "next/image";

import { EventCard } from "@/components/layout/EventCard";
import { GridContainer } from "@/components/layout/GridContainer";
import { RichTextField } from "@/components/typography/RichTextField";
import { ExpandableText } from "@/components/typography/ExpandableText";
import type { AssetStoryblok, MultilinkStoryblok, EventStoryblok } from "@/component-types-sb";
import { parseBerlinDate } from "@/lib/utils/berlinDate";
import { startOfDayISO } from "@/lib/utils/date";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import { fetchEventsAndTravels } from "@/lib/storyblok/fetchList";


type ExpertStoryblok = {
  name: string;
  title?: string;
  text?: string;
  image?: AssetStoryblok;
  instagram_link?: MultilinkStoryblok;
  linkedin_link?: MultilinkStoryblok;
  _uid: string;
  component: "expert";
  [k: string]: any;
};

type ResolvedExpert = ExpertStoryblok & { href?: string; storyUuid?: string };

function resolveExpert(
  item: string | ExpertStoryblok | { content: ExpertStoryblok; full_slug?: string; uuid?: string; [k: string]: any },
): ResolvedExpert | null {
  if (typeof item === "string") return null;
  if ("content" in item && item.content?.component === "expert") {
    return {
      ...item.content,
      href: item.full_slug ? `/${item.full_slug}` : undefined,
      storyUuid: item.uuid,
    };
  }
  if (item.component === "expert") {
    return item as ExpertStoryblok;
  }
  return null;
}

type ExpertItem = string | ExpertStoryblok | { content: ExpertStoryblok; full_slug?: string; uuid?: string; [k: string]: any };

type ExpertDetailStoryblok = {
  expert?: ExpertItem;
  experts?: ExpertItem[];
  event?: { content: EventStoryblok; full_slug?: string; [k: string]: any } | string;
  show_next_event?: boolean;
  _uid: string;
  component: "expert_detail";
  [k: string]: any;
};

type ExpertDetailProps = {
  blok: ExpertDetailStoryblok;
};

type EventData = {
  eventType: string;
  memberOnly: boolean;
  date: Date;
  title: string;
  ctaLink: string;
};

// Fetches the next upcoming event for a given expert
const useNextEvent = (expertUuid: string | undefined, enabled: boolean) => {
  const [event, setEvent] = useState<EventData | null>(null);

  useEffect(() => {
    if (!enabled || !expertUuid) return;

    const fetchNextEvent = async () => {
      const now = startOfDayISO();

      try {
        // Experten stehen auch auf Reisen — beide Typen abfragen.
        const stories = await fetchEventsAndTravels({
          filter_query: {
            date: { gt_date: now },
            experts: { in_array: expertUuid },
          },
          sort_by: "content.date:asc",
          per_page: 1,
          exact: true,
        });

        if (stories && stories.length > 0) {
          const story = stories[0];
          const content = story.content as EventStoryblok;
          setEvent({
            eventType: eventTypeFromContent(content),
                memberOnly: (content as { member_access?: string }).member_access === "member_only",
            date: parseBerlinDate(content.date),
            title: content.title,
            ctaLink: `/${story.full_slug}`,
          });
        }
      } catch (error) {
        console.error("Error fetching next event for expert:", error);
      }
    };

    fetchNextEvent();
  }, [enabled, expertUuid]);

  return event;
};

// Single expert card with its own event
const ExpertCard = ({
  expert,
  manualEvent,
  showNextEvent,
}: {
  expert: ResolvedExpert;
  manualEvent: EventData | null;
  showNextEvent: boolean;
}) => {
  const fetchedEvent = useNextEvent(
    expert.storyUuid,
    showNextEvent && !manualEvent,
  );
  const event = manualEvent || fetchedEvent;

  const eventSlot = event ? (
    <div className="flex flex-col gap-2 w-full">
      <h3 className="text-headline-md text-black">Nächstes Event</h3>
      <EventCard
        variant="densed"
        eventType={event.eventType}
                        memberOnly={event.memberOnly}
        date={event.date}
        title={event.title}
        ctaLink={event.ctaLink}
        className="max-w-none"
      />
    </div>
  ) : null;

  return (
    <div className="bg-white rounded-3xl p-6 flex flex-col gap-6 w-full">
      {/* Mobile: stacked layout */}
      <div className="flex flex-col gap-6 md:hidden">
        {expert.image?.filename && (
          <div className="relative w-full aspect-[345/155] rounded-2xl overflow-hidden">
            <Image
              src={expert.image.filename}
              alt=""
              fill
              sizes="100vw"
              className="object-cover blur-xl scale-110"
              aria-hidden="true"
            />
            <Image
              src={expert.image.filename}
              alt={expert.image.alt || expert.name || "Expert"}
              fill
              sizes="100vw"
              className="object-contain"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 items-center text-center">
          <div className="flex flex-col text-black">
            {expert.name && <h3 className="text-headline-xl">{expert.name}</h3>}
            {expert.title && <p className="text-copy">{expert.title}</p>}
          </div>
          {expert.text && (
            <ExpandableText lines={4} className="text-center">
              <RichTextField value={expert.text} className="text-copy text-black" />
            </ExpandableText>
          )}
          {eventSlot}
        </div>
      </div>

      {/* Tablet+: two-column layout */}
      <div className="hidden md:flex md:flex-row gap-12 items-start">
        {expert.image?.filename && (
          <div className="relative w-[200px] aspect-square rounded-2xl overflow-hidden shrink-0">
            <Image
              src={expert.image.filename}
              alt={expert.image.alt || expert.name || "Expert"}
              fill
              sizes="200px"
              className="object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 items-start text-left flex-1">
          <div className="flex flex-col text-black">
            {expert.name && <h3 className="text-headline-xl">{expert.name}</h3>}
            {expert.title && <p className="text-copy">{expert.title}</p>}
          </div>
          {expert.text && (
            <ExpandableText lines={4}>
              <RichTextField value={expert.text} className="text-copy text-black" />
            </ExpandableText>
          )}
          {eventSlot}
        </div>
      </div>
    </div>
  );
};

const ExpertDetail = ({ blok }: ExpertDetailProps) => {
  // Show single expert first, then additional experts from array
  const rawExperts: ExpertItem[] = [
    ...(blok.expert ? [blok.expert] : []),
    ...(blok.experts || []),
  ];
  const resolved = rawExperts.map(resolveExpert).filter(Boolean) as ResolvedExpert[];
  // Deduplicate by _uid in case same expert is in both fields
  const seen = new Set<string>();
  const experts = resolved.filter((e) => {
    if (seen.has(e._uid)) return false;
    seen.add(e._uid);
    return true;
  });
  const showNextEvent = blok.show_next_event !== false; // default true

  // Manual event from story reference (only applies to first expert)
  const eventStory = blok.event && typeof blok.event !== "string" ? blok.event : undefined;
  const eventContent = eventStory?.content as EventStoryblok | undefined;
  const manualEvent: EventData | null =
    eventContent && eventStory
      ? {
          eventType: eventTypeFromContent(eventContent),
          memberOnly: (eventContent as { member_access?: string }).member_access === "member_only",
          date: parseBerlinDate(eventContent.date),
          title: eventContent.title,
          ctaLink: `/${eventStory.full_slug}`,
        }
      : null;

  if (experts.length === 0) return null;

  return (
    <div {...storyblokEditable(blok)} className="w-full">
      <section className="py-10 flex flex-col gap-4 items-center w-full">
        <h2 className="text-headline-xl text-black text-center">
          Speaker
        </h2>

        <GridContainer className="!px-0">
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 px-4 md:px-0 flex flex-col gap-4">
            {experts.map((expert, i) => (
              <ExpertCard
                key={expert._uid}
                expert={expert}
                manualEvent={i === 0 ? manualEvent : null}
                showNextEvent={showNextEvent}
              />
            ))}
          </div>
        </GridContainer>
      </section>
    </div>
  );
};

export default ExpertDetail;
